import { memo } from "react";
import { ALL_ACCOUNTS } from "@helm/core";
import { ALL_ACCOUNTS_NOTE, PICK_ACCOUNT, TRADING_ACCOUNT, TRADING_ACCOUNT_SHARED } from "@helm/ui";
import { setAccountSelection, useSelectionSlice } from "../viewmodel/account";

/**
 * The order surface's account picker (SPEC §9.2 "account selector"; owner 2026-10-04: kept on every
 * entry surface, compact, at the start of its order row). It shows, by name, the account orders
 * from this surface go to. It reads and writes the ONE selection the top bar shows — every panel
 * trades the same account, and picking one here changes it everywhere (the menu says so) — so
 * routing is unchanged (`entryAccountOf`). "All accounts" is view-only for orders: the picker then
 * says "Pick account". Its words are the status-line picker's ("Trading account", "Trading account
 * for every panel"; @helm/ui statusLine.ts), so both say the same thing.
 */
export const AccountSelect = memo(function AccountSelect(): React.JSX.Element {
  const tv = useSelectionSlice();
  const all = tv.selection === ALL_ACCOUNTS;
  const none = tv.accounts.length === 0;
  return (
    <select
      className={`select select-sm account-select${all || none ? " account-select-unset" : ""}`}
      aria-label={TRADING_ACCOUNT}
      data-account={all ? "" : (tv.selection ?? "")}
      disabled={none}
      title={
        none
          ? "Connect a Rithmic login to pick an account"
          : all
            ? `${ALL_ACCOUNTS_NOTE} Orders from here need one account.`
            : `${TRADING_ACCOUNT_SHARED}: orders from here go to it. Picking an account here changes it in the status line and every panel.`
      }
      value={all ? ALL_ACCOUNTS : (tv.selection ?? "")}
      onChange={(ev) => {
        setAccountSelection(ev.target.value);
      }}
    >
      {none ? (
        <option value="">No account</option>
      ) : (
        <>
          {all && (
            <option value={ALL_ACCOUNTS} disabled>
              {PICK_ACCOUNT}
            </option>
          )}
          {/* Visible in the open menu: the selection is shared, not this panel's own. */}
          <optgroup label={TRADING_ACCOUNT_SHARED}>
            {tv.accounts.map((a) => (
              <option key={a.summary.id} value={a.summary.id}>
                {a.summary.label}
              </option>
            ))}
          </optgroup>
        </>
      )}
    </select>
  );
});
